import {useEffect, useRef, useState} from 'react'
import {effect} from '../shared/effect'
import {isAppRoute} from '../shared/app-routes'
import {useIsMobile} from './use-is-mobile'
import {useIs1024} from './use-is-1024'

export function useHideAwarePosition(currentPosition, currentHidden, currentContent, currentFlipVertically = false) {
    const prevHiddenRef = useRef(true)
    const [position, setPosition] = useState(currentPosition)
    const [flipVertically, setFlipVertically] = useState(currentFlipVertically)
    const [hidden, setHidden] = useState(true)
    const [noTransition, setNoTransition] = useState(false)
    const [content, setContent] = useState(currentContent)
    const isMobile = useIsMobile()
    const is1024 = useIs1024()
    const currentContentRef = useRef(currentContent)
    currentContentRef.current = currentContent
    const animationTime = isMobile ? 270 : 0
    const backdropFilterEnabled = !(isAppRoute() && is1024)

    useEffect(() => {
        const wasHidden = prevHiddenRef.current
        prevHiddenRef.current = currentHidden

        if (!wasHidden && currentHidden) {
            setNoTransition(false)
            setHidden(true)
            if (animationTime) {
                const timer = setTimeout(() => {
                    setPosition(currentPosition)
                    setFlipVertically(currentFlipVertically)
                }, animationTime)
                return () => clearTimeout(timer)
            } else {
                setPosition(currentPosition)
                setFlipVertically(currentFlipVertically)
            }
        } else if (wasHidden && !currentHidden) {
            setPosition(currentPosition)
            setFlipVertically(currentFlipVertically)
            setNoTransition(true)
            const raf = requestAnimationFrame(() => {
                setNoTransition(false)
                setHidden(false)
            })
            return () => cancelAnimationFrame(raf)
        } else {
            setPosition(currentPosition)
            setFlipVertically(currentFlipVertically)
            setHidden(currentHidden)
            setNoTransition(false)
        }
    }, [currentHidden, currentPosition, currentFlipVertically, animationTime])

    effect([currentContent], () => {
        if (currentHidden && !hidden) {
            if(animationTime) setTimeout(() => {
                setContent(currentContentRef.current)
            }, animationTime)
            else setContent(currentContentRef.current)
        } else if (!currentHidden && hidden) {
            setContent(currentContent)
        } else {
            setContent(currentContent)
        }
    })

    const preparingToShow = prevHiddenRef.current && !currentHidden
    return {
        position,
        flipVertically: preparingToShow ? currentFlipVertically : flipVertically,
        hidden,
        noTransition: preparingToShow || noTransition,
        content,
        backdropFilterEnabled,
    }
}
